<template>
  <tr class="bg-light-3 text-base dark:border-dark-5 dark:bg-dark-3">
    <td
      :class="[
        'w-[20%] max-w-sm text-ellipsis whitespace-nowrap px-6 py-4 pl-6',
        { 'rounded-bl-xl pb-6': isLast },
      ]"
    >
      {{ installationPointType }}
    </td>
    <td :class="['w-[80%] py-4', { 'pb-6': isLast }]">
      <em
        v-if="impedimentType"
        class="mdi mr-1"
        :class="impedimentIconClass(impedimentType)"
        :title="impedimentTypeLabel"
        :aria-label="impedimentTypeLabel"
        data-testid="failedInstallationAttemptType"
      />
      {{ reason }}
    </td>
    <td :class="['relative px-6 py-4', { 'rounded-br-xl pb-6': isLast }]">
      <OptionsDropdown
        icon="mdi-dots-vertical"
        direction-x="left"
        direction="down"
      >
        <li
          v-if="
            useOrderStore().isTkdOrder() &&
            isSrtDevice &&
            installationPoint.services.length > 0
          "
          data-testid="tkdServiceReportButton"
          @click="onTkdService(installationPoint, true)"
        >
          <em class="mdi mdi-file-document mr-2" />Service Report
        </li>
        <li
          data-testid="exchangeDeviceButton"
          @click="onExchange(installationPoint)"
        >
          <em class="mdi mdi-swap-horizontal mr-2" />Montieren
        </li>
        <li
          :class="[
            {
              'cursor-not-allowed text-light-disabled-text hover:bg-transparent dark:text-dark-disabled-text':
                !showEdit,
            },
          ]"
          data-testid="editBuildingButton"
          @click="
            showEdit ? onEdit(installationPoint) : showDisabledNotification()
          "
        >
          <em class="mdi mdi-pencil mr-2" />Bearbeiten
        </li>
        <li
          v-if="showDelete"
          data-testid="deleteBuildingButton"
          @click="onDelete(installationPoint)"
        >
          <em class="mdi mdi-delete mr-2" />Löschen
        </li>
      </OptionsDropdown>
    </td>
  </tr>
</template>

<script setup lang="ts">
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { deviceTypeDetails, deviceTypes } from '@/enums/device';
import { computed } from 'vue';
import OptionsDropdown from '@/components/UI/Dropdown/OptionsDropdown.vue';
import { DeviceType } from '@/models/devices/Device';
import { useOrderStore } from '@/store/order/orderStore';
import { EntityTransactionType } from '@/models/OrderChanges';
import { truncate } from '@/helpers/truncateString';
import { impedimentIconClass, impedimentTypes } from '@/enums/generic';
import { useNotificationStore } from '@/store/notifications/notificationStore';
import { Notification } from '@/models/Notification';

const installationPointType = computed(() => {
  return deviceTypeDetails[
    props.installationPoint?.supportedDeviceType as DeviceType
  ].shortLabel;
});

const props = defineProps({
  installationPoint: {
    type: InstallationPoint,
    required: true,
  },
  onEdit: {
    type: Function,
    required: true,
  },
  onDelete: {
    type: Function,
    required: false,
    default: undefined,
  },
  onExchange: {
    type: Function,
    required: true,
  },
  onTkdService: {
    type: Function,
    required: true,
  },
  isLast: {
    type: Boolean,
    default: false,
  },
});

const showEdit = computed(() => {
  return useOrderStore().hasTransaction(
    props.installationPoint?.id,
    EntityTransactionType.CREATE_ENTITY
  );
});

const showDelete = computed(() => {
  const isUnmountedDevice = props.installationPoint.isDeviceUnmounted();
  return showEdit.value && !isUnmountedDevice;
});

const showDisabledNotification = () => {
  useNotificationStore().addNotification(
    new Notification(
      'Dieses Element wurde in einem früheren Auftrag erstellt und kann hier nicht bearbeitet werden.',
      '',
      'info'
    )
  );
};

const isSrtDevice = computed(() => {
  if (props.installationPoint.supportedDeviceType) {
    return (
      props.installationPoint.supportedDeviceType ===
      deviceTypes.SmartRadiatorThermostat
    );
  }
  return false;
});

const impedimentType = computed(() => {
  return props.installationPoint?.failedInstallationAttempt?.type;
});

const impedimentTypeLabel = computed(() => {
  return impedimentTypes.find((t) => t.value === impedimentType.value)?.name;
});

const reason = computed(() => {
  const ip = props.installationPoint;
  if (!ip) return;

  if (ip.failedInstallationAttempt?.reason) {
    return ip.failedInstallationAttempt.reason;
  }

  const latestUnmountEvent = ip.getLatestUnmountEvent();

  return latestUnmountEvent
    ? truncate(latestUnmountEvent.reason, 40)
    : undefined;
});
</script>
